import { Builder } from '@builder.io/sdk';
import { S11AwardsSection } from '../components/interactive-sections/S11Awards';
import { BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES } from '../BuilderComponentsName';
import {
    backgroundColorOptions,
    backgroundGradientOptions,
    textColorOptionsLimited,
} from '../utils/builderColorOptions';
import { getBackgroundGradientInput, getPaddingInputs, getBackgroundColorInput } from '../utils/builderSharedInputs';

Builder.registerComponent(S11AwardsSection, {
    name: BUILDER_INTERACTIVE_SECTION_COMPONENTS_NAMES.S11Awards,
    friendlyName: 'S11 Awards/As Seen In',
    inputs: [
        {
            name: 'variant',
            type: 'string',
            friendlyName: 'Display Variant',
            enum: [
                { label: 'Default (Full Cards)', value: 'default' },
                { label: 'Compact (Logo Only)', value: 'compact' },
            ],
            defaultValue: 'default',
            helperText:
                'Default: Shows full award cards with text and pagination. Compact: Shows logo-only cards with auto-scroll, no pagination (ideal for "As Seen In" sections)',
        },
        {
            name: 'headline',
            type: 'string',
            friendlyName: 'Headline',
            required: true,
            defaultValue: 'Awards & Recognition',
            helperText: 'The main heading displayed at the top of the awards section',
        },
        {
            name: 'headlineColor',
            type: 'string',
            friendlyName: 'Headline Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Blue',
            helperText: 'Select a color for the headline text',
        },
        {
            name: 'awards',
            friendlyName: 'Awards',
            type: 'list',
            required: true,
            onChange: (options) => {
                const currentAwards = options.get('awards');
                const awardCount = currentAwards.length;

                // Check if the list has more than 12 awards
                if (awardCount > 12) {
                    options.set('awards', currentAwards.slice(0, 12));
                    alert('Maximum 12 awards allowed. Extra awards have been removed.');
                }

                // Check if less than minimum (but only if they're trying to go below 4)
                if (awardCount < 4 && awardCount > 0) {
                    alert('Minimum 4 awards required. Please add more awards.');
                }
            },
            defaultValue: [
                {
                    image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F6fbe8bedff7045848cc4ee269ca0a3b0',
                    imageAltText: 'Award recognition',
                    link: '/awards',
                    subheadline: 'Best Of Marathon 2019',
                    text: 'Wausau, WI',
                },
                {
                    image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fa8006080ee6b4f92bf08cebb3c267a38',
                    imageAltText: 'Award recognition 2',
                    link: '/awards',
                    subheadline: 'Best Of Lansing 2022',
                    text: 'Lansing, MI',
                },
                {
                    image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2F06134a7f01224c339842b33d9f2e4b8b',
                    imageAltText: 'Award recognition 3',
                    link: '/awards',
                    subheadline: 'Inc. 5000 - 2023',
                    text: 'Inc. 5000 - 2023 ',
                },
                {
                    image: 'https://cdn.builder.io/api/v1/image/assets%2F10266dac478d4c768f6b6fbe586ae30e%2Fbdde006b26d44f41a89f1cafd880eb2c',
                    imageAltText: 'Best of Miami - 2024',
                    link: '/awards',
                    subheadline: 'Industry Leadership',
                    text: 'Miami, FL',
                },
            ],
            helperText: 'Add awards with images, links, and descriptions (minimum: 4, maximum: 12)',
            subFields: [
                {
                    name: 'image',
                    type: 'file',
                    allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
                    friendlyName: 'Award Image',
                    required: true,
                    helperText: 'Upload the award image',
                },
                {
                    name: 'imageAltText',
                    type: 'string',
                    friendlyName: 'Image Alt Text',
                    required: true,
                    helperText: 'Alt text for the award image (for accessibility)',
                },
                {
                    name: 'link',
                    type: 'string',
                    friendlyName: 'Award Link',
                    required: false,
                    helperText: 'URL link for the award (optional)',
                },
                {
                    name: 'subheadline',
                    type: 'string',
                    friendlyName: 'Award Subheadline',
                    required: false,
                    helperText: 'Award subheadline or title (optional - leave empty for compact variant)',
                },
                {
                    name: 'text',
                    type: 'string',
                    friendlyName: 'Award Description',
                    required: false,
                    helperText: 'Award description text (optional - leave empty for compact variant)',
                },
            ],
        },
        {
            name: 'subheadlineColor',
            type: 'string',
            friendlyName: 'Subheadline Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Blue',
            helperText: 'Select a color for the subheadline text (only visible in default variant)',
        },
        {
            name: 'textColor',
            type: 'string',
            friendlyName: 'Text Color',
            enum: textColorOptionsLimited,
            defaultValue: 'Navy',
            helperText: 'Select a color for the main text content (only visible in default variant)',
        },
        {
            name: 'cardBackgroundColor',
            type: 'string',
            friendlyName: 'Card Background Color',
            enum: backgroundColorOptions,
            defaultValue: 'TrueWhite',
            helperText: 'Select the background color for award cards',
        },
        ...getBackgroundColorInput({
            defaultValue: 'White',
            backgroundColorsList: backgroundColorOptions,
        }),
        ...getBackgroundGradientInput({
            backgroundGradientList: backgroundGradientOptions,
        }),
        {
            name: 'backgroundImage',
            type: 'file',
            allowedFileTypes: ['jpeg', 'jpg', 'png', 'svg', 'webp'],
            friendlyName: 'Background Image',
            helperText: 'Upload an image to use as the section background',
        },
        ...getPaddingInputs({
            desktopTop: 'large',
            desktopBottom: 'large',
            mobileTop: 'small',
            mobileBottom: 'none',
        }),
    ],
});
